import React, { useEffect, useRef, useState } from 'react';
import { useFeatures } from '../utils/features';
import { loadTaskPallets } from '../features/queue/palletBeacons';

// Reopen the Laser Start popup (utils/palletBeaconsPrompt: the task's sales-order pallets and their beacons)
// after it was closed, 2026-10-26 (Mike, after the beacon lifecycle review: the popup showed once, at the
// first Start, and could not be brought back). Reads GET /tasks/{id}/pallet_beacons as things are NOW; nothing
// is lit until someone presses Find or Light again in the popup. Shown wherever pallet beacons are on, for a
// task that has been started (before that its parts are routed onto no pallet).

const styles = {
  wrap: { marginTop: 8 },
  msg: { fontSize: '0.85rem', marginTop: 4 },
};

export const NO_PALLETS_TEXT = "Nothing to show: none of this task's pallets is still being packed (or pallet beacons are switched off).";

/** Whether the button shows for a task in this status: started at least once (Pending has routed nothing). */
export function showTaskPalletsButton(status) {
  return !!status && status !== 'Pending';
}

export default function TaskPalletsButton({ taskId, status, openPrompt = null, className = '' }) {
  const enabled = useFeatures().palletBeacons.enabled;
  const [busy, setBusy] = useState(''); // 'read' | 'open' (the popup is up)
  const [msg, setMsg] = useState(null); // {tone, text}
  const opening = useRef(false);           // one popup at a time: held until the popup closes
  const shownTask = useRef(taskId);
  shownTask.current = taskId;

  useEffect(() => { setMsg(null); }, [taskId]);

  if (!enabled || !taskId || !showTaskPalletsButton(status)) return null;

  const open = async () => {
    if (opening.current) return;
    opening.current = true;
    const forTask = taskId;
    setBusy('read');
    setMsg(null);
    try {
      const r = await loadTaskPallets(forTask);
      if (shownTask.current !== forTask) return;
      if (!r.ok || !r.body) {
        setMsg({ tone: 'danger', text: `Couldn't read the pallets: ${r.message || 'try again'}` });
        return;
      }
      const pallets = Array.isArray(r.body.pallets) ? r.body.pallets : [];
      if (!pallets.length) {
        setMsg({ tone: 'secondary', text: NO_PALLETS_TEXT });
        return;
      }
      const prompt = openPrompt || ((opts) => import('../utils/palletBeaconsPrompt').then((m) => m.default(opts)));
      setBusy('open');
      await prompt({ taskId: forTask, data: { task_id: forTask, pallets }, reopened: true });
    } finally {
      opening.current = false;
      setBusy('');
    }
  };

  return (
    <div className={className} style={styles.wrap}>
      <button type="button" className="btn btn-outline-primary btn-sm" onClick={open} disabled={!!busy}
        data-testid="task-pallets-button">
        {busy === 'read' ? 'Reading the pallets…' : 'Pallets for this task'}
      </button>
      {msg && <div className={`text-${msg.tone}`} style={styles.msg} role="status">{msg.text}</div>}
    </div>
  );
}
